import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState
} from 'react';

import { Capacitor } from '@capacitor/core';
import { Keyboard } from '@capacitor/keyboard';

interface KeyboardContextProps {
  isKeyboardOpen: boolean;
  keyboardWillShow: boolean;
  keyboardDidShow: boolean;
  keyboardDidHide: boolean;
}

const KeyboardContext = createContext<KeyboardContextProps | undefined>(
  undefined
);

export const KeyboardProvider: React.FC<{ children: React.ReactNode }> = ({
  children
}) => {
  const [isKeyboardOpen, setIsKeyboardOpen] = useState<boolean>(false);
  const [keyboardWillShow, setKeyboardWillShow] = useState<boolean>(false);
  const [keyboardDidShow, setKeyboardDidShow] = useState<boolean>(false);
  const [keyboardDidHide, setKeyboardDidHide] = useState<boolean>(false);

  const handleKeyboardWillShow = useCallback(() => {
    setKeyboardWillShow(true);
    setKeyboardDidShow(false);
    setKeyboardDidHide(false);
  }, []);

  const handleKeyboardDidShow = useCallback(() => {
    setIsKeyboardOpen(true);
    setKeyboardWillShow(false);
    setKeyboardDidShow(true);
    setKeyboardDidHide(false);
  }, []);

  const handleKeyboardDidHide = useCallback(() => {
    setIsKeyboardOpen(false);
    setKeyboardWillShow(false);
    setKeyboardDidShow(false);
    setKeyboardDidHide(true);
  }, []);

  useEffect(() => {
    if (Capacitor.isNativePlatform()) {
      Keyboard.addListener('keyboardWillShow', handleKeyboardWillShow);
      Keyboard.addListener('keyboardDidShow', handleKeyboardDidShow);
      Keyboard.addListener('keyboardDidHide', handleKeyboardDidHide);
    } else {
      window.addEventListener('keyboardWillShow', handleKeyboardWillShow);
      window.addEventListener('keyboardDidShow', handleKeyboardDidShow);
      window.addEventListener('keyboardDidHide', handleKeyboardDidHide);
    }

    return () => {
      if (Capacitor.isNativePlatform()) {
        Keyboard.removeAllListeners();
      } else {
        window.removeEventListener('keyboardWillShow', handleKeyboardWillShow);
        window.removeEventListener('keyboardDidShow', handleKeyboardDidShow);
        window.removeEventListener('keyboardDidHide', handleKeyboardDidHide);
      }
    };
  }, [handleKeyboardWillShow, handleKeyboardDidShow, handleKeyboardDidHide]);

  return (
    <KeyboardContext.Provider
      value={{
        isKeyboardOpen,
        keyboardWillShow,
        keyboardDidShow,
        keyboardDidHide
      }}
    >
      {children}
    </KeyboardContext.Provider>
  );
};

export const useKeyboard = (): KeyboardContextProps => {
  const context = useContext(KeyboardContext);
  if (!context) {
    throw new Error('useKeyboard must be used within a KeyboardProvider');
  }
  return context;
};
